import * as React from "react";

import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/copy-button";
import { cn } from "@/lib/utils";

// Collapsible pretty-printed JSON block used by raw-payload detail views
// (e.g. the fireblocks-events detail page's row-expand). Composes
// CopyButton rather than re-implementing clipboard logic.

export interface JsonViewerProps {
  data: unknown;
  defaultCollapsed?: boolean;
  /**
   * When true, the component stretches to fill its flex parent instead of
   * capping itself at a fixed height — the payload then scrolls within its
   * own box rather than growing (or being capped independently of) a
   * dialog that already manages its own fixed height/scroll. Requires the
   * parent chain to be a `flex flex-col` with `min-h-0` down to this
   * component, same as fireblocks-events's detail dialog sets up.
   */
  fill?: boolean;
}

const FALLBACK_TEXT = "Unable to display payload";

export function JsonViewer({ data, defaultCollapsed = true, fill = false }: JsonViewerProps) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  // JSON.stringify throws synchronously on circular references or BigInt
  // values — guard it so a malformed payload can't crash the component tree.
  let pretty: string;
  let stringifyFailed = false;
  try {
    pretty = JSON.stringify(data, null, 2);
  } catch {
    pretty = FALLBACK_TEXT;
    stringifyFailed = true;
  }

  return (
    <div className={cn("rounded-md border border-border", fill && "flex min-h-0 flex-1 flex-col")}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-2 py-1">
        <Button type="button" variant="ghost" size="sm" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? "Show raw payload" : "Hide raw payload"}
        </Button>
        {!stringifyFailed && <CopyButton value={pretty} label="Copy raw payload" />}
      </div>
      {!collapsed && (
        <pre className={cn("overflow-auto p-3 font-mono text-xs", fill ? "min-h-0 flex-1" : "max-h-80")}>
          {pretty}
        </pre>
      )}
    </div>
  );
}
